import React, { useCallback, useEffect, useState, useRef } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { Controller } from "react-hook-form";
import axios from "axios";
import { useSelector } from "react-redux";
import { debounce } from "../../../../styles/styles";
import { backend_api } from "../../../../static/server";

const UserApiField = ({
  name,
  label = "User",
  control,
  errors,
  isRequired = false,
  apiUrl = `${backend_api}team/create-employee/`,
  showSelfOption = false,
  selfOptionLabel = "Me (Self)",
  defaultUser = null,
}) => {
  const { token } = useSelector((state) => state.user);

  // Create selfOption inside component to access selfOptionLabel
  const selfOption = {
    id: "self",
    first_name: selfOptionLabel,
    last_name: "",
    is_self: true,
  };

  const [options, setOptions] = useState(() => {
    const initial = [];
    if (showSelfOption) initial.push(selfOption);
    if (defaultUser) initial.push(defaultUser);
    return initial;
  });
  const [nextUrl, setNextUrl] = useState(apiUrl);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isSelected, setIsSelected] = useState(false);
  const lastSearchRef = useRef("");

  const fetchUsers = useCallback(
    async (url) => {
      if (!url || loading) return;
      setLoading(true);
      try {
        const res = await axios.get(url, {
          headers: { Authorization: `Token ${token}` },
        });

        const results = res.data.results || [];

        setOptions((prev) => {
          if (url === `${apiUrl}?search=${searchText}`) {
            // Fresh search - add self option at the beginning if enabled
            return showSelfOption ? [selfOption, ...results] : results;
          } else {
            // Pagination/scroll - just append new results, keep existing options (including self)
            return [...prev, ...results];
          }
        });
        setNextUrl(res.data.next);
      } catch (err) {
        console.error("Failed to load users", err);
      } finally {
        setLoading(false);
      }
    },
    [token, loading, searchText, apiUrl, showSelfOption, selfOption]
  );

  useEffect(() => {
    if (defaultUser && !options.some((o) => o.id === defaultUser.id)) {
      setOptions((prev) => [...prev, defaultUser]);
    }
  }, [defaultUser, options]);

  useEffect(() => {
    // Only fetch if search text has changed and no item is selected
    if (!isSelected && searchText !== lastSearchRef.current) {
      lastSearchRef.current = searchText;
      fetchUsers(`${apiUrl}?search=${searchText}`);
    }
  }, [fetchUsers, searchText, isSelected, apiUrl]);

  const handleScroll = useCallback(
    (event) => {
      if (
        nextUrl &&
        !loading &&
        event.currentTarget.scrollTop + event.currentTarget.clientHeight >=
          event.currentTarget.scrollHeight - 20
      ) {
        fetchUsers(nextUrl);
      }
    },
    [fetchUsers, nextUrl, loading]
  );

  const debouncedSearch = useCallback(
    debounce((value) => {
      if (!isSelected) {
        setOptions(showSelfOption ? [selfOption] : []); // Reset to initial state
        setNextUrl(`${apiUrl}?search=${value}`);
        setSearchText(value);
      }
    }, 300),
    [isSelected, apiUrl, showSelfOption, selfOption]
  );

  const handleInputChange = (_, value, reason) => {
    // Only trigger search if user is typing, not when selecting
    if (reason === "input" && !isSelected) {
      debouncedSearch(value || "");
    }
  };

  const handleChange = (_, val, field) => {
    setIsSelected(!!val);

    // If self option is selected, return null for form value
    if (val && val.is_self) {
      field.onChange(null);
      return;
    }

    // Update form field with user ID or null
    field.onChange(val ? val.id : null);
  };

  const handleOpen = () => {
    setIsSelected(false);
    // Reset search when opening dropdown
    if (options.length === 0 || (showSelfOption && options.length === 1)) {
      setSearchText("");
      setNextUrl(apiUrl);
      fetchUsers(apiUrl);
    }
  };

  const getOptionLabel = (option) => {
    if (option.is_self) {
      return option.first_name;
    }
    return `${option.first_name || ""} ${option.last_name || ""}`.trim();
  };

  const findSelectedOption = (fieldValue) => {
    if (fieldValue === null || fieldValue === undefined) {
      return showSelfOption ? selfOption : null;
    }
    return options.find((o) => o.id === fieldValue) || null;
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={isRequired ? { required: `${label} is required` } : {}}
      render={({ field }) => (
        <Autocomplete
          {...field}
          options={options}
          getOptionLabel={getOptionLabel}
          value={findSelectedOption(field.value)}
          onChange={(_, val) => handleChange(_, val, field)}
          onInputChange={handleInputChange}
          onOpen={handleOpen}
          ListboxProps={{
            onScroll: handleScroll,
            style: { maxHeight: 250, overflowY: "auto" },
          }}
          loading={loading}
          isOptionEqualToValue={(opt, val) => {
            if (opt.is_self && val.is_self) return true;
            return opt.id === val.id;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                <span>
                  {label}
                  {isRequired && <span style={{ color: "red" }}> *</span>}
                </span>
              }
              size="small"
              error={Boolean(errors?.[name])}
              helperText={errors?.[name]?.message}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.is_self ? (
                <span style={{ fontWeight: "500" }}>
                  {getOptionLabel(option)}
                </span>
              ) : (
                getOptionLabel(option)
              )}
            </li>
          )}
        />
      )}
    />
  );
};

export default UserApiField;
